<template>
  <div class="extension-column">
    <!-- 概算伝票の導線はカラムをスクロールしても見失わないよう見出しごと上端に固定する -->
    <div class="extension-column__header">
      <div class="overview-sec extension-column__header-title">
        次の延長
      </div>

      <div class="extension-column__header-actions">
        <Preview :slip-svg="slipSvg" />

        <print-btn
          subject="概算伝票"
          :print="printEstimatedSlip"
        />
      </div>
    </div>

    <!-- NOTE: 自動延長の発火窓では手動延長を止める（同じサイクルで二重に延長されるのを防ぐ） -->
    <p
      v-if="isAutoExtensionProcessing && extensionCandidates.length > 0"
      class="extension-column__blocked ma-0 mb-2"
    >
      自動延長の処理中です。反映されるまで手動の延長はできません。
    </p>

    <!-- 設定中プランは候補一覧を置き換えず、同じコースの候補カードの内容を上書きして並ぶ -->
    <div v-if="extensionCandidates.length > 0">
      <extension-preset-card
        v-for="(extensionCandidate, extensionCandidateIdx) in extensionCandidates"
        :key="`extensionCandidates[${extensionCandidateIdx}]`"
        :candidate="extensionCandidate"
        :estimated-total="getTotal(extensionCandidateIdx)"
        :is-calculating="isCalculating"
        :recommended="extensionCandidateIdx === recommendedIndex"
        :plan-applied="isPlanApplied(extensionCandidate)"
        :releasing-plan="isReleasingPlan(extensionCandidate)"
        :submitting="submittingIdx === extensionCandidateIdx"
        :disabled="isAutoExtensionProcessing"
        @submit="submitExtension(extensionCandidateIdx)"
        @edit="openCandidateEditor(extensionCandidateIdx)"
        @release-plan="releaseHeldPlan(extensionCandidate)"
      />
    </div>

    <div v-else>
      <v-skeleton-loader
        v-if="isInitializing"
        height="160"
        type="card"
      />

      <p
        v-else
        class="ma-0 ml-2 pa-0 text-sm text--secondary"
      >
        延長候補はありません
      </p>
    </div>

    <extension-edit-dialog
      v-if="editingSeed"
      :seed="editingSeed"
      :table-id="tableId"
      :table-number="tableNumber"
      :table-prefix="tablePrefix"
      :table-suffix="tableSuffix"
      :custom-name="customName"
      :start-at="startAt"
      :expire-at="expireAt"
      :customer-count="customerCount"
      :total="total"
      :paid-in-advance-amount="paidInAdvanceAmount"
      :total-deposit-amount="totalDepositAmount"
      :disabled="isAutoExtensionProcessing"
      @held="handlePlanHeld($event)"
      @extended="applyExtensionResult($event)"
      @close="editingSeed = null"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  inject,
  watch,
  onUnmounted,
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api'
import {
  findIndex,
  forEach,
  includes,
  isEmpty,
  map,
  reject,
} from 'lodash'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import ExtensionPlanApi from '@/api/waiter/ExtensionPlan'
import useExtensionCandidates, { pickRecommendedProposalIndex } from '@/views/composable/useExtensionCandidates'
import { useAutoExtensionStatus } from '@/views/composable/useAutoExtensionCountdown'
import useExtensionSlipPrint from '@/views/composable/useExtensionSlipPrint'
import useCurrentData from '@/views/composable/useCurrentData'
import useKitchenPrint from '@/views/composable/useKitchenPrint'
import useVendingHistory from '@/views/composable/useVendingHistory'
import {
  normalizeExtensionPlans,
  buildSeedFromCandidate,
  buildSeedFromExtensionPlan,
} from '@/views/composable/useExtensionPlan'
import ExtensionPresetCard from './ExtensionPresetCard.vue'
import ExtensionEditDialog from './ExtensionEditDialog.vue'
import PrintBtn from '@/views/components/util/PrintBtn.vue'
import Preview from '@/views/components/printer/Preview.vue'

export default {
  components: {
    ExtensionPresetCard,
    ExtensionEditDialog,
    PrintBtn,
    Preview,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    hallName: {
      type: String,
      default: '',
    },
    // NOTE: 卓詳細(getTable)のattributes.extensionPlans。コースごとに1件で、
    // 中身のキーだけスネークケースで届く
    extensionPlans: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const vendingHistories = inject('vendingHistories', ref([]))

    const { currentUser } = useCurrentData()
    const { printKitchenSlips } = useKitchenPrint()
    const { buildVendingHistoryTemp } = useVendingHistory()

    const submittingIdx = ref(null)
    // NOTE: 解除は候補カードごとに出るので、どのコースを解除中かで持つ
    const releasingPlanCourseId = ref(null)
    const editingSeed = ref(null)

    // NOTE: 保持/即延長の結果は即座に画面へ反映したいので、propsを初期値にローカルで持つ
    const heldPlans = ref(normalizeExtensionPlans(props.extensionPlans))
    watch(() => props.extensionPlans, newVal => {
      heldPlans.value = normalizeExtensionPlans(newVal)
    })

    const {
      extensionProposals,
      extensionCandidates,
      isInitializing,
      estimatedTotals,
      isCalculating,
      vendingHistoriesToSubmitOfArray,
      fetchExtensionProposals,
      initVendingHistoriesTemp,
      getTotalTax,
      getTotal,
      resetCandidates,
      recalculateEstimations,
    } = useExtensionCandidates(props)

    const courseVendibleIdOf = extensionCandidate => {
      return Number(extensionCandidate.courseHistoryTemp.value.vendibleId)
    }

    // NOTE: プランはコースごとに1件。上書きしたカードだけ「設定中」バッジ・解除導線を出す
    const planCourseVendibleIds = computed(() => map(heldPlans.value, plan => Number(plan.course.vendibleId)))
    const isPlanApplied = extensionCandidate => {
      return includes(planCourseVendibleIds.value, courseVendibleIdOf(extensionCandidate))
    }
    const isReleasingPlan = extensionCandidate => {
      return releasingPlanCourseId.value === courseVendibleIdOf(extensionCandidate)
    }

    const buildCandidateFromPlan = plan => {
      const seed = buildSeedFromExtensionPlan(plan, buildVendingHistoryTemp)

      return {
        courseHistoryTemp: seed.course,
        nominationHistoriesTemp: seed.nominations,
        itemHistoriesTemp: seed.items,
        newHistoriesTemp: [],
      }
    }

    // NOTE: プランは候補一覧を置き換えるのではなく、同じコースの候補カードの内容を上書きする。
    // 候補はAPIから取り直されるので、取得・プラン変更のたびに組み直してから重ね直す
    const applyHeldPlansToCandidates = () => {
      if (isEmpty(heldPlans.value)) return

      // NOTE: 配列ごと差し替えて、見積りパイプラインのwatchに上書き後の内容を拾わせる
      const overwritten = [...extensionCandidates.value]
      const appended = []

      forEach(heldPlans.value, plan => {
        const planCandidate = buildCandidateFromPlan(plan)
        const targetIdx = findIndex(overwritten, extensionCandidate => {
          return courseVendibleIdOf(extensionCandidate) === Number(plan.course.vendibleId)
        })

        if (targetIdx >= 0) {
          overwritten[targetIdx] = planCandidate
        } else {
          // NOTE: コース差し替えでプランのコースが候補に無い場合は、同じカードUIで先頭に足す
          appended.push(planCandidate)
        }
      })

      extensionCandidates.value = [...appended, ...overwritten]

      // NOTE: 差し替えた候補は税サが未計算(null)に戻るため、見積りパイプラインを明示的に回す。
      // watch任せだと単価・税区分が変わっていない上書きで再計算が走らず、
      // 上書きしていない候補まで概算が出なくなる
      recalculateEstimations()
    }

    const refreshCandidates = async () => {
      await fetchExtensionProposals()
      applyHeldPlansToCandidates()
    }

    // NOTE: プランの設定・解除・内容変更は候補を取り直さずに重ね直すだけで足りる
    watch(heldPlans, () => {
      initVendingHistoriesTemp(extensionProposals.value)
      applyHeldPlansToCandidates()

      // NOTE: 全解除時は上書きが無く applyHeldPlansToCandidates 側の再計算も走らないので、ここで回す
      if (isEmpty(heldPlans.value)) recalculateEstimations()
    })

    // NOTE: プランで先頭に足したカードの分だけindexがずれるため、提案ではなく候補側で「前回と同じ」を探す
    const recommendedIndex = computed(() => {
      const candidateCourses = map(extensionCandidates.value, extensionCandidate => ({
        course: { vendibleId: extensionCandidate.courseHistoryTemp.value.vendibleId },
      }))

      return pickRecommendedProposalIndex(candidateCourses, vendingHistories.value)
    })

    // NOTE: 自動延長の発火窓（発火予定を過ぎて反映待ちの間）は手動延長を止める。
    // 判定は予告カウントダウンと同じ composable を共有し、条件を二重に持たない。
    const { isProcessing: isAutoExtensionProcessing } = useAutoExtensionStatus({
      enabled: () => props.autoExtensionEnabled,
      expireAt: () => props.expireAt,
      courseName: () => props.autoExtensionCourseName,
    })

    // NOTE: 延長作成が通ったあとの共通後処理。候補カード・編集モーダルの
    // どの経路から延長しても同じ反映（明細更新・キッチン印刷・候補の取り直し）になるようにまとめている
    const applyExtensionResult = async ({ vendingHistories: createdVendingHistories, table }) => {
      emit('updated:vending-histories', createdVendingHistories.data)
      // NOTE: updated:tableでprops.totalが変化しwatchが発火するが、
      // submittingIdxガードでblockされるので二重フェッチしない
      emit('updated:table', table.data)

      // キッチンプリンター印刷（既存の注文フローと同等）
      const tableCode = props.customName || `${props.tablePrefix}${props.tableNumber}${props.tableSuffix}`
      printKitchenSlips({
        vendingHistories: createdVendingHistories.data,
        staffName: currentUser.value?.name || '',
        hallName: props.hallName,
        tableCode,
        customerCount: props.customerCount,
      })

      vm.$toast.success('延長しました')

      // NOTE: プランは延長しても消費しない（卓が終わるまで有効）。
      // 卓の再取得(getTable)でpropsが更新されれば、そちらの内容に追従する

      // NOTE: 延長後は候補が変わるため取り直す
      resetCandidates()
      await refreshCandidates()
    }

    // NOTE: 確認モーダルを挟まず即時にバックエンドへ延長データを作成する。
    // プランで上書きされたカードも同じ経路（上書き後のtemp）で注文される
    const submitExtension = async index => {
      if (submittingIdx.value !== null) return
      if (isAutoExtensionProcessing.value) return

      submittingIdx.value = index

      try {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: vendingHistoriesToSubmitOfArray.value[index],
        })

        if (res?.status === 200) {
          await applyExtensionResult(res.data)
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長に失敗しました')
      } finally {
        submittingIdx.value = null
      }
    }

    const openCandidateEditor = index => {
      editingSeed.value = buildSeedFromCandidate(extensionCandidates.value[index])
    }

    // NOTE: サーバーはコース単位でupsertする（別コースのプランは残る）ので、保存後の一覧は卓の内容を正とする
    const handlePlanHeld = ({ table }) => {
      heldPlans.value = normalizeExtensionPlans(table?.attributes?.extensionPlans)
    }

    // NOTE: プランは卓が終わるまで残り自動延長にも効くので、明示的に取り消す導線が要る。
    // 解除するのは押されたカードのコースのプランだけで、他コースのプランは残す
    const releaseHeldPlan = async extensionCandidate => {
      if (releasingPlanCourseId.value !== null) return

      const courseVendibleId = courseVendibleIdOf(extensionCandidate)
      releasingPlanCourseId.value = courseVendibleId

      try {
        const res = await ExtensionPlanApi.deleteExtensionPlan({ tableId: props.tableId, courseVendibleId })

        if (res?.status === 200) {
          heldPlans.value = reject(heldPlans.value, plan => Number(plan.course.vendibleId) === courseVendibleId)
          vm.$toast.success('延長の設定を解除しました')
        } else {
          vm.$toast.error('設定の解除に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('設定の解除に失敗しました')
      } finally {
        releasingPlanCourseId.value = null
      }
    }

    // NOTE: 取り消し・注文・客数変更などで卓の合計が変わったら見積が古くなるため候補ごと取り直す
    // （延長実行中はsubmittingIdxガードでblockされ二重取得しない）
    watch(() => props.total, async () => {
      if (submittingIdx.value !== null) return

      resetCandidates()
      await refreshCandidates()
    })

    // NOTE: YONAREZI 3 概算伝票。プランは候補に重ねてあるので、伝票のソースは候補に一本化できる
    const { slipSvg, printEstimatedSlip } = useExtensionSlipPrint({
      props,
      extensionCandidates,
      estimatedTotals,
      getTotalTax,
    })

    onUnmounted(() => resetCandidates())

    onMounted(async () => {
      await refreshCandidates()
    })

    return {
      extensionCandidates,
      isInitializing,
      isCalculating,
      recommendedIndex,
      submittingIdx,
      isAutoExtensionProcessing,
      slipSvg,
      editingSeed,

      isPlanApplied,
      isReleasingPlan,
      getTotal,
      submitExtension,
      releaseHeldPlan,
      openCandidateEditor,
      handlePlanHeld,
      applyExtensionResult,
      printEstimatedSlip,
    }
  },
}
</script>

<style lang="scss" scoped>
// スクロールコンテナは親の .overview-col（padding: 16px 16px 4px）。
// 負マージンでその余白ぶんを打ち消し、上端に貼り付いても下のカードが透けないよう地色を敷く
.extension-column__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -16px -16px 10px;
  padding: 14px 16px 8px;
  background: var(--overview-bg, #eef0f6);
}

.extension-column__header-title {
  margin-bottom: 0;
}

.extension-column__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;

  // 見出し行に収めるため、既存の印刷・プレビューボタンを小さめに揃える
  ::v-deep .v-btn {
    height: 30px;
    min-width: 0;
    padding: 0 8px;
    font-size: 11px;
  }

  ::v-deep .v-btn .v-icon {
    font-size: 16px;
    margin-right: 2px;
  }
}

.extension-column__blocked {
  font-size: 13px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--overview-surface-3, rgba(255, 255, 255, 0.06));
  color: var(--overview-text-1, inherit);
}
</style>
